import type { UploadRequestOption } from '@rc-component/upload/lib/interface';
import { Upload, type UploadFile, type UploadProps } from 'antd';
import cn from 'classnames';
import { ConfigProvider } from 'lib/antd';
import type { Image } from 'models';
import { useTranslation } from 'react-i18next';
import type { UploadedFile } from 'models/uploaded-file';
import { Col } from '../../col';
import { Icon } from '../../icon';
import { message } from '../../message';
import { Row } from '../../row';
import { Text } from '../../typography';
import type { UploadChangeParam as UploadChangeParameter } from '../../upload';
import { failedUpload, unexpectedError } from './default-validations';
import { type ImageDraggerProperties, ImageUpload } from './image-upload';

export type FileUpload = UploadFile<UploadedFile>;
export type FileUploadDone<T extends { filename?: string; id: string; url: string } = Image> = {
  response: T;
  status: 'done';
} & Omit<FileUpload, 'response' | 'status'>;
export type UploadedFileList = FileUpload[];

const { Dragger } = Upload;

const ImageDragger = <T extends { url: string }>({
  alt,
  className,
  fileList,
  loadImage,
  onChange,
  shape = 'rectangle',
  size = 'medium',
  uploadFile,
  validations,
  ...props
}: ImageDraggerProperties<T>) => {
  const { t } = useTranslation();

  const handleChange = (info: UploadChangeParameter): void => {
    if (info.file.status === 'error') {
      const fileName = info.file.name;
      const failedUploadValidation = validations?.find((validation) => validation.key === failedUpload.key);
      if (failedUploadValidation) {
        message.error(t(failedUploadValidation.message, { fileName }));
      }
    }

    if (onChange) {
      onChange(info);
    }
  };

  const defaultProps: UploadProps<T> = {
    accept: 'image/*',
    async customRequest({ file, onError, onProgress, onSuccess }: UploadRequestOption<T>) {
      if (typeof file === 'string') {
        const unexpectedErrorValidation = validations?.find((validation) => validation.key === unexpectedError.key);
        if (unexpectedErrorValidation) {
          onError?.(new Error(unexpectedErrorValidation.key));
        }

        return;
      }

      const failedFileValidation = validations?.find((validation) =>
        validation.isValidFile ? !validation.isValidFile(file) : false,
      );

      if (failedFileValidation) {
        onError?.(new Error(failedFileValidation.key));
        return;
      }

      try {
        const dimensions: HTMLImageElement = await loadImage(file);
        const failedImageValidation = validations?.find((validation) =>
          validation.isValidImage ? !validation.isValidImage(dimensions) : false,
        );

        if (failedImageValidation) {
          onError?.(new Error(failedImageValidation.key));
          return;
        }

        try {
          const response = await uploadFile(file, (eventValue) => {
            onProgress?.({ ...eventValue });
          });

          onSuccess?.(response);
        } catch {
          const failedUploadValidation = validations?.find((validation) => validation.key === failedUpload.key);
          if (failedUploadValidation) {
            onError?.(new Error(failedUploadValidation.key));
          }
        }
      } catch {
        const unexpectedErrorValidation = validations?.find((validation) => validation.key === unexpectedError.key);
        if (unexpectedErrorValidation) {
          onError?.(new Error(unexpectedErrorValidation.key));
        }
      }
    },
    fileList,
    listType: 'picture-card',
    maxCount: 1,
    multiple: false,
    onChange: handleChange,
    showUploadList: false,
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Upload: {
            padding: 8,
          },
        },
      }}
    >
      <Dragger<T>
        className={cn(
          'image-input',
          {
            'image-input--circle': shape === 'circle',
            'image-input--large': size === 'large',
            'image-input--medium': size === 'medium',
            'image-input--rectangle': shape === 'rectangle',
            'image-input--rectangle-stretch': shape === 'rectangle-stretch',
            'image-input--small': size === 'small',
            'image-input--square': shape === 'square',
          },
          className,
        )}
        fileList={fileList}
        onChange={handleChange}
        {...defaultProps}
        {...props}
      >
        {fileList?.[0] && fileList[0].status !== 'error' ? (
          <ImageUpload alt={alt} value={fileList[0]} />
        ) : (
          <Row align="middle" gutter={[0, 'xs']} justify="center">
            <Col span={24}>
              <Icon icon="pic-line" size={size === 'medium' ? 'middle' : size} />
            </Col>
            {size === 'small' ? null : (
              <Col span={24}>
                <Text size={size === 'large' ? 'large' : 'small'} weight="medium">
                  Click or drag file to this area to upload
                </Text>
              </Col>
            )}
          </Row>
        )}
      </Dragger>
    </ConfigProvider>
  );
};

export default ImageDragger;
